import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from 'components/Headers/Header';
import {
  Button,
  Card,
  CardHeader,
  Col,
  Container,
  Input,
  InputGroup,
  Row,
  Table,
} from 'reactstrap';
import toast from 'react-hot-toast';
import api from 'services/api';
import { CircularProgress, Popper, TextField } from '@material-ui/core';
import FloatingButton from 'components/FloatingButton';
import Swal from 'sweetalert2';
import ProdutoAtividade from 'models/ProdutoAtividade';
import { socket } from 'config/socket';
import { useAuth } from 'hooks/auth';
import { Autocomplete } from '@material-ui/lab';
import traduzirProdutoAtividade from 'utils/traduzirProdutoAtividade';

interface LocationState {
  propostaId: number;
  orcamentoId: number;
  emprCodigo: string;
  geral: boolean | undefined;
}

const NUMERO_MAXIMO_ITENS_AVULSOS = 20;

const styles = () => ({
  popper: {
    width: 'fit-content',
  },
});

const PopperMy = function (props: any) {
  return <Popper {...props} style={styles().popper} placement="bottom-start" />;
};

const ConfiguradorItensAvulsosNovo: React.FC = () => {
  const location = useLocation<LocationState>();
  const propostaVersaoId = location.state?.propostaId ?? null;
  const orcamentoId = location.state?.orcamentoId ?? null;
  const emprCodigo = location.state?.emprCodigo ?? '';
  const geral = location.state?.geral;
  const [atividades, setAtividades] = useState<ProdutoAtividade[]>([]);
  const [atividadeSelecionada, setAtividadeSelecionada] = useState<
    number | null
  >();
  const [sequenciaSelecionada, setSequenciaSelecionada] = useState<
    number | null
  >();
  const { usuario } = useAuth();

  const [errors, setErrors] = useState<Record<number, string[]>>({});

  const [isLoading, setIsLoading] = useState(false);

  const [materiaisAvulsos, setMateriaisAvulsos] = useState(
    Array.from({ length: NUMERO_MAXIMO_ITENS_AVULSOS }, () => ({
      item: '',
      quantidade: '',
      sequencia: '',
      atividade: '',
    })),
  );

  async function listaAtividades() {
    const produtoData = await api.get(`/produto/configuracao/${orcamentoId}`);
    const produto = produtoData.data[0];
    const response = await api.get(
      `/produto-atividade?produtoId=${produto.id}`,
    );
    setAtividades(response.data);
  }

  async function checkCookies() {
    const atividadeS = localStorage.getItem('atividade_selecionada');
    const SequenciaS = localStorage.getItem('sequencia_selecionada');

    if (atividadeS) setAtividadeSelecionada(Number(atividadeS));
    if (SequenciaS) setSequenciaSelecionada(Number(SequenciaS));
  }

  useEffect(() => {
    listaAtividades();
    checkCookies();
  }, []);

  useEffect(() => {
    const joinRoom = () => {
      socket.emit('join_config', {
        configId: orcamentoId,
        user: {
          id: usuario.id,
          nome: usuario.nome,
        },
      });
    };

    if (socket.connected) {
      joinRoom();
    }

    // socket.emit('join_config', {
    //   configId: orcamentoId,
    //   user: {
    //     id: usuario.id,
    //     nome: usuario.nome,
    //   },
    // });

    socket.on('connect', joinRoom);

    return () => {
      socket.emit('leave_config', orcamentoId);
      socket.off('connect', joinRoom);
      socket.off('config_updated');
    };
  }, []);

  const handleInputChange = (
    index: number,
    campo: keyof typeof materiaisAvulsos[number],
    valor: string,
  ) => {
    const materiaisAvulsosAtualizados = [...materiaisAvulsos];
    materiaisAvulsosAtualizados[index][campo] = valor;
    setMateriaisAvulsos(materiaisAvulsosAtualizados);

    // Retira a mensagem de erro quando começa a digitar no campo
    if (errors[index]) {
      const updatedErrors = { ...errors };
      updatedErrors[index] = updatedErrors[index].filter(
        field => field !== campo,
      );
      if (updatedErrors[index].length === 0) {
        delete updatedErrors[index];
      }
      setErrors(updatedErrors);
    }
  };

  function aplicarSeqAtiv() {
    localStorage.setItem('atividade_selecionada', String(atividadeSelecionada));
    localStorage.setItem('sequencia_selecionada', String(sequenciaSelecionada));

    const ativ = atividades.find(ati => ati.id === atividadeSelecionada);

    const novaLista = materiaisAvulsos.map(item => {
      if (!item.item || !item.quantidade) {
        return item;
      }
      return {
        ...item,
        atividade: String(ativ?.codSAP || ativ?.codSGE),
        sequencia: String(sequenciaSelecionada),
      };
    });

    setMateriaisAvulsos(novaLista);
  }

  async function validaRows() {
    // coluna e quais campos estão faltando
    const errorsArr: Record<number, string[]> = {};
    let temUmaLinhaValida = false;

    materiaisAvulsos.forEach((material, index) => {
      const temValor = Object.values(material).some(
        value => value && String(value)?.trim() !== '',
      );
      if (temValor) {
        const camposFaltando = Object.keys(material).filter(
          key =>
            material[key as keyof typeof material] &&
            String(material[key as keyof typeof material])?.trim() === '',
        );

        if (camposFaltando.length > 0) {
          errorsArr[index] = camposFaltando;
        } else {
          temUmaLinhaValida = true;
        }
      }
    });

    const isEmpty = (value: any) => !value || String(value).trim() === '';

    // Caso falte uma coluna a ser preenchida, é considerado inválido
    const itensInvalidos = materiaisAvulsos.filter(item => {
      const valores = [
        item.item,
        item.sequencia,
        item.atividade,
        item.quantidade,
      ];

      const todosVazios = valores.every(isEmpty);
      const todosPreenchidos = valores.every(v => !isEmpty(v));

      return !todosVazios && !todosPreenchidos;
    });

    setErrors(errorsArr);

    if (!temUmaLinhaValida) {
      toast.error('Não há nenhum material para adicionar.');
      setIsLoading(false);
      return;
    }

    if (Object.keys(errorsArr).length > 0 || itensInvalidos.length > 0) {
      toast.error('Alguns campos não foram preenchidos');
      setIsLoading(false);
      return;
    }

    const isProposta = propostaVersaoId && propostaVersaoId !== 0;

    const url = isProposta
      ? `/integracao/item-lote`
      : `/integracao/orcamento/item-lote`;

    Swal.fire({
      title: `Deseja incluir os materiais?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sim`,
      confirmButtonColor: '#d33',
      cancelButtonText: `Não`,
    }).then(async result => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const response = await api.post(`${url}`, {
            rows: materiaisAvulsos.map(it => {
              return {
                ...it,
                item: String(it.item).trim(),
              };
            }),
            emprCodigo: emprCodigo ?? '',
            geral,
            propostaId: propostaVersaoId,
            orcamentoId,
          });

          const retorno: {
            sucesso: boolean | false;
            totalSolicitado: number;
            totalSucesso: number;
            itensValidos: any[];
            itensInvalidos: any[];
          } = response.data;

          setIsLoading(false);
          if (retorno.sucesso) {
            setMateriaisAvulsos(
              Array.from({ length: NUMERO_MAXIMO_ITENS_AVULSOS }, () => ({
                item: '',
                quantidade: '',
                sequencia: '',
                atividade: '',
              })),
            );
            toast.success('Materiais adicionados com sucesso');
          } else {
            Swal.fire({
              icon: 'success',
              title: `${retorno.totalSucesso} de ${retorno.totalSolicitado} materiais foram inseridos.`,
              text: `Os demais, não tiveram sucesso. Eles ficaram na tabela para avaliação. Verifique se o código está correto e se o item está ativo.`,
            });

            setMateriaisAvulsos([
              ...retorno.itensInvalidos?.map((item: any) => {
                return {
                  item: item.codigo,
                  quantidade: item.quantidade,
                  atividade: item.atividade,
                  sequencia: item.sequencia,
                };
              }),
              ...Array.from(
                {
                  length:
                    NUMERO_MAXIMO_ITENS_AVULSOS -
                    (retorno.totalSolicitado - retorno.totalSucesso),
                },
                () => ({
                  item: '',
                  quantidade: '',
                  sequencia: '',
                  atividade: '',
                }),
              ),
            ]);
          }

          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          toast.error('Erro ao salvar os materiais.');
        }
      }
    });
  }

  return (
    <>
      <Header showCards={false} />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="">
                {/* <CardHeader className="border-0 d-flex justify-content-between align-items-center"> */}
                <Row>
                  <Col sm="8">
                    <h3 className="mb-0">
                      Cadastro de materiais avulsos em lote
                    </h3>
                  </Col>

                  <Col sm="2">
                    <div>
                      <Autocomplete
                        style={{ display: 'flex', alignItems: 'center' }}
                        options={atividades}
                        PopperComponent={PopperMy}
                        getOptionLabel={option =>
                          traduzirProdutoAtividade(option)
                        }
                        value={
                          atividades.find(
                            atividade => atividade.id === atividadeSelecionada,
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setAtividadeSelecionada(
                            newValue ? newValue.id : null,
                          );
                        }}
                        renderOption={option => (
                          <>
                            {traduzirProdutoAtividade(option)}
                            {option.codSGE &&
                              ` (Ref. DomSGE: ${option.codSGE})`}
                          </>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Atividade"
                            className="m-0"
                            margin="dense"
                            variant="outlined"
                          />
                        )}
                      />
                    </div>
                  </Col>

                  <Col sm="1">
                    <TextField
                      fullWidth
                      label="Sequência"
                      variant="outlined"
                      id="outlined-basic"
                      inputProps={{ min: 0 }}
                      size="small"
                      value={sequenciaSelecionada ?? ''}
                      type="number"
                      // maxLength={2}
                      onChange={e => {
                        const value = Number(e.target.value);

                        if (value >= 0 || e.target.value === '') {
                          setSequenciaSelecionada(value);
                        }
                      }}
                    />
                  </Col>

                  <Col sm="1">
                    <Button
                      // className="my-4"
                      color="primary"
                      type="button"
                      onClick={aplicarSeqAtiv}
                    >
                      Aplicar
                    </Button>
                  </Col>

                  <FloatingButton
                    disabled={isLoading}
                    color="primary"
                    onClick={validaRows}
                    className="custom-button"
                  >
                    <>
                      <small className="p-2">Incluir</small>
                      {isLoading && (
                        <CircularProgress
                          className="mt-2 ml-2"
                          color="inherit"
                          size={10}
                        />
                      )}
                    </>
                  </FloatingButton>
                </Row>
              </CardHeader>
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantidade</th>
                    <th>Sequência</th>
                    <th>Atividade</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={3} className="text-center">
                        <CircularProgress color="inherit" size={30} />
                      </td>
                    </tr>
                  ) : (
                    materiaisAvulsos.map((material, index) => (
                      <tr key={index}>
                        <td>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type="text"
                              value={material.item}
                              onChange={e =>
                                handleInputChange(index, 'item', e.target.value)
                              }
                            />
                          </InputGroup>
                          {errors[index]?.includes('item') && (
                            <div className="text-danger">
                              Item é obrigatório
                            </div>
                          )}
                        </td>
                        <td>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type="number"
                              value={material.quantidade}
                              onChange={e =>
                                handleInputChange(
                                  index,
                                  'quantidade',
                                  e.target.value,
                                )
                              }
                            />
                          </InputGroup>
                          {errors[index]?.includes('quantidade') && (
                            <div className="text-danger">
                              Quantidade é obrigatório
                            </div>
                          )}
                        </td>
                        <td>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type="number"
                              value={material.sequencia}
                              onChange={e =>
                                handleInputChange(
                                  index,
                                  'sequencia',
                                  e.target.value,
                                )
                              }
                            />
                          </InputGroup>
                          {errors[index]?.includes('sequencia') && (
                            <div className="text-danger">
                              Sequencia é obrigatório
                            </div>
                          )}
                        </td>
                        <td>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type="text"
                              value={material.atividade}
                              onChange={e =>
                                handleInputChange(
                                  index,
                                  'atividade',
                                  e.target.value,
                                )
                              }
                            />
                          </InputGroup>
                          {errors[index]?.includes('atividade') && (
                            <div className="text-danger">
                              Atividade é obrigatório
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default ConfiguradorItensAvulsosNovo;
